import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Upload, Calendar, CheckCircle, UserPlus, PieChart, Mail, Zap, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import Gradient from '@/assets/medihub_gradient_mesh_turqoise.jpg';
import shot1 from '@/assets/Screenshot 2025-03-31 at 0.04.34.png';
import shot2 from '@/assets/Screenshot 2025-03-31 at 0.06.41.png';
import shot3 from '@/assets/Screenshot 2025-03-31 at 0.07.22.png';
import CalendarPreviewSection from '@/components/home/CalendarPreviewSection';
import { fetchMyEvents } from '@/services/events';
import { toast } from '@/hooks/use-toast';
import { Event } from '@/lib/types';
const SHOW_TESTIMONIALS = false;

const OrganizerGuide = () => {

  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const apiEvents = await fetchMyEvents();
        setEvents(apiEvents);

      } catch (error) {
        console.error('Error loading events and recommendations:', error);
        toast({
          title: "Hiba történt",
          description: "Az események betöltése sikertelen",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  return <div className="min-h-screen relative">
    <Navbar />
    <section className="text-center pt-32 md:pt-48 pb-28 relative z-20 ">
      <div className="absolute inset-0 z-0">
        <img
          src={Gradient}
          alt="MediHub háttér"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30 z-0"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <span className="inline-block text-white font-semibold tracking-wider mb-3 bg-black/60 backdrop-blur-sm px-6 py-2 rounded-full border border-white/10">
            SZERVEZŐKNEK
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold mb-8 text-white hero-heading">
          Minden egészségügyi rendezvény egy helyen
        </h1>
        <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-12 leading-relaxed">
          A MediHub célja, hogy minden szakmai rendezvény egy helyen legyen, elérje a megfelelő közönséget és elkerülhetők legyenek az időpontütközések, így növelve a részvételt. Kövesse az alábbi egyszerű lépéseket!          </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register/organizer">
            <Button size="lg" className="bg-medihub-accent hover:bg-medihub-accent/90 shadow-lg hover:shadow-xl transition-all duration-300 scale-100 hover:scale-105">
              <UserPlus className="mr-2 h-5 w-5" />
              Regisztrálok szervezőként
            </Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" className="border-2 border-white text-black hover:text-white hover:bg-white/10 shadow-md hover:shadow-lg transition-all duration-300 scale-100 hover:scale-105">
              <LogIn className="mr-2 h-5 w-5" />
              Bejelentkezés
            </Button>
          </Link>
        </div>
      </div>
    </section>

    <main className="container mt-16 mx-auto px-4 relative z-10">

      {/* Benefits Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-10">Miért válaszd a MediHub-ot?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          <Card className="border-t-4 border-t-medihub-primary">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <div className="bg-medihub-primary/10 p-3 rounded-full mb-4">
                  <PieChart className="h-8 w-8 text-medihub-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">Ütközések elkerülése</h3>
                <p className="text-gray-600">Kerülje az átfedéseket, növelje a résztvevők számát!</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-medihub-primary">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <div className="bg-medihub-primary/10 p-3 rounded-full mb-4">
                  <Zap className="h-8 w-8 text-medihub-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">Egyszerű használat</h3>
                <p className="text-gray-600">Felhasználóbarát felület, percek alatt feltöltheti és kezelheti az eseményeit.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-t-4 border-t-medihub-primary">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <div className="bg-medihub-primary/10 p-3 rounded-full mb-4">
                  <Trophy className="h-8 w-8 text-medihub-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">Nagyobb láthatóság</h3>
                <p className="text-gray-600">Eseményei közvetlenül a célközönség elé kerülnek, növelve a résztvevők számát.</p>
              </div>
            </CardContent>
          </Card>



        </div>
      </section>

      <CalendarPreviewSection events={events} isLoading={isLoading} />

      {/* Step by Step Guide */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-10">3 egyszerű lépés a sikerhez</h2>

        <Tabs defaultValue="register" className="md:max-w-6xl mx-auto">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="register">1. Regisztráció</TabsTrigger>
            <TabsTrigger value="profile">2. Profil kitöltése</TabsTrigger>
            <TabsTrigger value="upload">3. Esemény feltöltése</TabsTrigger>
            {/*             <TabsTrigger value="monitor">4. Nyomonkövetés</TabsTrigger>
 */}          </TabsList>

          <TabsContent value="register" className="mt-6">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Regisztrálj szervezőként</h3>
                <p className="text-gray-600 mb-4">
                  A regisztráció mindössze 2 percet vesz igénybe. Töltse ki az űrlapot, hogy hozzáférjen a szervezői funkciókhoz.                </p>
                <ol className="space-y-3 list-decimal list-inside text-gray-700">
                  <li>Kattintson a "Regisztrálok szervezőként" gombra</li>
                  <li>Adja meg az e-mail címét és válasszon ki egy erős jelszót</li>
                  <li>Töltse ki a szervezeti alapadatokat</li>
                  <li>Erősítse meg az e-mail címét a megadott e-mail címre kapott linkkel</li>
                </ol>
                <div className="mt-6">
                  <Link to="/register/organizer">
                    <Button className="bg-medihub-accent hover:bg-medihub-accent/90">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Szervezői regisztráció
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md">
                <img src={shot1} alt="Regisztrációs oldal képernyőfotó" className="rounded-lg border border-gray-200" />
                <p className="text-sm text-gray-500 mt-2 italic text-center">A regisztrációs oldal mindössze pár egyszerű mezőt tartalmaz</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Töltse ki szervezői profilját</h3>
                <p className="text-gray-600 mb-4">
                  Az adatok egyszerűen feltölthetőek, és később bármikor szerkeszthetőek. A szerkesztés a szervezői profilban érhető el.                </p>
                <ol className="space-y-3 list-decimal list-inside text-gray-700">
                  <li>Lépjen a "Szervezői profil" menüpontra</li>
                  <li>Adja meg a szervezete adatait (leírás, logo, webhely)</li>
                  <li>Állítsa be a kapcsolatattartási adatait</li>
                  <li>Mentse el a változtatásokat</li>
                </ol>
                {/*                 <div className="mt-6 flex items-center space-x-2 bg-blue-50 p-3 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Tipp:</strong> A teljes profil kitöltése növeli az eseményeid megjelenési esélyét a kiemelt listákban!
                  </p>
                </div>
 */}              </div>
              <div className="bg-white p-4 rounded-xl shadow-md">
                <img src={shot2} alt="Regisztrációs oldal képernyőfotó" className="rounded-lg border border-gray-200" />
                <p className="text-sm text-gray-500 mt-2 italic text-center">Profil szerkesztési nézet</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-6">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Töltse fel az eseményeit</h3>
                <p className="text-gray-600 mb-4">
                  Az események feltöltésére egy strukturált űrlap érhető el, amely végigvezeti Önt minden szükséges információn.                </p>
                <ol className="space-y-3 list-decimal list-inside text-gray-700">
                  <li>Kattintson az "Új esemény létrehozása" gombra a Szervező profilon belül</li>
                  <li>Adja meg a rendezvény alapvető adatait (név, helyszín, dátum)</li>
                  <li>Válassza ki a célközönséget és az esemény típusát</li>
                </ol>
                <div className="mt-6 flex items-center space-x-2 bg-amber-50 p-3 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Tipp:</strong> Írjon  részletes leírást az eseményeidhez a nagyobb érdeklődés érdekében!
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md">
                <img src={shot3} alt="Regisztrációs oldal képernyőfotó" className="rounded-lg border border-gray-200" />
                <p className="text-sm text-gray-500 mt-2 italic text-center">Esemény feltöltési nézet</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="monitor" className="mt-6">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Kövesd nyomon az eredményeket</h3>
                <p className="text-gray-600 mb-4">
                  A MediHub részletes statisztikákat biztosít az eseményeid teljesítményéről.
                  Kövesd nyomon a megtekintéseket és a regisztrációk számát.
                </p>
                <ol className="space-y-3 list-decimal list-inside text-gray-700">
                  <li>A Dashboard-on láthatod az összes eseményed statisztikáit.</li>
                  <li>Kövesd nyomon a megtekintések és átkattintások számát.</li>
                  <li>Elemezd a konverziós arányokat a regisztrációk alapján.</li>
                  <li>Exportáld a statisztikákat riportok készítéséhez.</li>
                  <li>Használd az adatokat a következő eseményeid optimalizálásához.</li>
                </ol>
                <div className="mt-6 flex items-center space-x-2 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Tipp:</strong> A statisztikák elemzésével azonosíthatod, mely eseménytípusok vonzzák a legtöbb résztvevőt!
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md">
                <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center">
                  <PieChart className="h-16 w-16 text-gray-400" />
                  <p className="text-gray-500">Statisztikai nézet</p>
                </div>
                <p className="text-sm text-gray-500 mt-2 italic text-center">A statisztikai dashboard átfogó képet ad az eseményeid teljesítményéről</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Testimonials - Optional Section */}
      {SHOW_TESTIMONIALS && (
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-10">Szervezők mondták rólunk</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-medihub-primary/20 flex items-center justify-center text-medihub-primary font-bold text-xl">
                      K
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold">Dr. Kovács Eszter</h4>
                      <p className="text-sm text-gray-500">Konferenciaszervező, KardioMed</p>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">
                    "A MediHub segítségével 40%-kal növeltük a résztvevők számát a kardiológiai konferenciánkon. Az egyszerű feltöltési folyamat és az automatikus értesítések rengeteg időt spóroltak nekünk."
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-medihub-primary/20 flex items-center justify-center text-medihub-primary font-bold text-xl">
                      T
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold">Török Zoltán</h4>
                      <p className="text-sm text-gray-500">Eseményszervező, MedEvent Kft.</p>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">
                    "Korábban több platformon kellett hirdetnünk eseményeinket, de a MediHub-bal egy helyen elérjük az összes releváns egészségügyi szakembert. A statisztikák segítenek a jövőbeli események tervezésében is."
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-medihub-primary/20 flex items-center justify-center text-medihub-primary font-bold text-xl">
                      N
                    </div>
                    <div className="ml-4">
                      <h4 className="font-bold">Nagy Katalin</h4>
                      <p className="text-sm text-gray-500">Marketing vezető, PharmaConf</p>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">
                    "A MediHub analitikai eszközei segítettek megérteni, mely tartalmaink vonzzák legjobban a szakembereket. Az eredmény: célzottabb marketing és több regisztráció a webinárjainkra."
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="rounded-2xl p-8 md:p-12 text-white relative overflow-hidden mb-16">
        <div className="absolute inset-0 z-0">
          <img
            src={Gradient}
            alt="MediHub háttér"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30 z-0"></div>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between relative z-10">
          <div className="mb-6 md:mb-0">
            <h2 className="text-3xl font-bold mb-4">Kezdj bele már ma!</h2>
            <p className="text-white/90 max-w-2xl">
              Növeld eseményeid láthatóságát és résztvevőid számát a MediHub platformján.
              A regisztráció ingyenes és mindössze 2 percet vesz igénybe.
            </p>
          </div>
          <div>
            <Link to="/register/organizer">
              <Button size="lg" variant="secondary" className="bg-white text-medihub-primary hover:bg-white/90">
                Ingyenes regisztráció
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>;
};
export default OrganizerGuide;
